import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Skeleton, Avatar, TextField, FormControl,
    InputLabel, Select, MenuItem, Tooltip
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const STATUS_STYLE = {
    present: { bg: '#F6FFED', border: '#52C41A', color: '#52C41A', label: 'Present' },
    late: { bg: '#FFF7E6', border: '#FA8C16', color: '#FA8C16', label: 'Late' },
    half_day: { bg: '#FFF7E6', border: '#FA8C16', color: '#FA8C16', label: 'Half Day' },
    absent: { bg: '#FFF1F0', border: '#F5222D', color: '#F5222D', label: 'Absent' },
    missing_punch: { bg: '#FFF1F0', border: '#F5222D', color: '#F5222D', label: 'Missing Punch' },
    on_leave: { bg: '#E6F4FF', border: '#0071BC', color: '#0071BC', label: 'On Leave' },
    week_off: { bg: '#FAFAFA', border: '#BFBFBF', color: '#6B7280', label: 'Week Off' },
    holiday: { bg: '#FAFAFA', border: '#BFBFBF', color: '#6B7280', label: 'Holiday' },
};

function StatusChip({ status }) {
    if (!status) {
        return <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Not marked</Typography>;
    }
    const s = STATUS_STYLE[status] || STATUS_STYLE.present;
    return (
        <Box sx={{
            display: 'inline-flex', justifyContent: 'center',
            backgroundColor: s.bg, border: '1px solid', borderColor: s.border,
            borderRadius: '2px', padding: '1px 8px', whiteSpace: 'nowrap'
        }}>
            <Typography sx={{ fontSize: '11px', lineHeight: '20px', color: s.color, textTransform: 'uppercase' }}>
                {s.label}
            </Typography>
        </Box>
    );
}

function StatCard({ label, value, hint, accent }) {
    return (
        <Box sx={{
            flex: 1, minWidth: '150px', backgroundColor: 'background.light',
            borderRadius: '12px', padding: '13px 16px',
            boxShadow: '0px 0px 25px 0px #00000012',
            borderLeft: '3px solid', borderColor: accent
        }}>
            <Typography sx={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.07em', color: 'text.secondary' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '22px', fontWeight: 600, lineHeight: 1.25 }}>{value}</Typography>
            <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>{hint}</Typography>
        </Box>
    );
}

function initialsOf(name) {
    if (!name) return '--';
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function formatWorked(minutes) {
    if (minutes === null || minutes === undefined) return '—';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${String(m).padStart(2, '0')}m`;
}

export function StaffAttendance() {
    const selectedHub = useSelector(state => state.user.selected_hub);
    const inventory = useSelector(state => state.inventory.inventory);
    const toast = useRef(null);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [monthLocked, setMonthLocked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [shiftFilter, setShiftFilter] = useState('');
    const [shifts, setShifts] = useState([]);

    // staff_id -> { arrival_time, dispersal_time, remarks }
    const [edits, setEdits] = useState({});

    const hubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };
        const fromStore = normalize(selectedHub);
        if (fromStore) return fromStore;
        const stored = localStorage.getItem('selected_hub');
        if (!stored) return null;
        try {
            return normalize(JSON.parse(stored));
        } catch (e) {
            return normalize(stored);
        }
    }, [selectedHub]);

    const dateParam = useMemo(() => dayjs(selectedDate).format('YYYY-MM-DD'), [selectedDate]);
    const editCount = Object.keys(edits).length;

    const showToast = (severity, summaryText, detail) => {
        if (toast.current) toast.current.show({ severity, summary: summaryText, detail, life: 3500 });
    };

    const errorMessage = (error, fallback) => {
        const detail = error?.response?.data?.error;
        if (!detail) return fallback;
        return typeof detail === 'string' ? detail : fallback;
    };

    useEffect(() => {
        if (!hubId) return;
        fetchGrid();
        fetchShifts();
    }, [hubId, dateParam]);

    const fetchShifts = async () => {
        try {
            const res = await CustomAxios.get('staff/shifts/', { params: { hub_id: hubId, is_active: true } });
            setShifts(res.data || []);
        } catch (error) {
            setShifts([]);
        }
    };

    const applyGridResponse = (data) => {
        setRows(data.rows || []);
        setSummary(data.summary || null);
        setMonthLocked(Boolean(data.is_locked));
        setEdits({});
    };

    const fetchGrid = async () => {
        try {
            setLoading(true);
            const res = await CustomAxios.get('staff/attendance/', {
                params: { hub_id: hubId, date: dateParam }
            });
            applyGridResponse(res.data);
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load attendance'));
            setRows([]);
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    const valueFor = (row, field) => {
        const edit = edits[row.staff_id];
        if (edit && field in edit) return edit[field] ?? '';
        return row[field] ?? '';
    };

    const handleEdit = (row, field, value) => {
        setEdits(prev => ({
            ...prev,
            [row.staff_id]: {
                arrival_time: row.arrival_time,
                dispersal_time: row.dispersal_time,
                remarks: row.remarks,
                ...prev[row.staff_id],
                [field]: value,
            },
        }));
    };

    const handleSave = async () => {
        if (!editCount) return;
        const payload = Object.entries(edits).map(([staffId, edit]) => ({
            staff_id: Number(staffId),
            arrival_time: edit.arrival_time || null,
            dispersal_time: edit.dispersal_time || null,
            remarks: edit.remarks || null,
            source: 'manual',
        }));

        try {
            setSaving(true);
            const res = await CustomAxios.post('staff/attendance/bulk-save/', {
                hub_id: hubId, date: dateParam, rows: payload,
            });
            applyGridResponse(res.data);
            showToast('success', 'Saved', res.data.message);
            if (res.data.skipped?.length) {
                showToast('warn', 'Some rows skipped',
                    `${res.data.skipped.length} row(s) are covered by an approved request and were left unchanged.`);
            }
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Save failed'));
        } finally {
            setSaving(false);
        }
    };

    const handleMarkAllPresent = async () => {
        if (!window.confirm('Fill shift-standard times for everyone still unmarked? Existing entries and approved leave are left alone.')) return;
        try {
            setSaving(true);
            const res = await CustomAxios.post('staff/attendance/mark-all-present/', {
                hub_id: hubId, date: dateParam, shift_id: shiftFilter || undefined,
            });
            applyGridResponse(res.data);
            showToast('success', 'Done', res.data.message);
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Operation failed'));
        } finally {
            setSaving(false);
        }
    };

    // Group by shift so the grid reads the way the hub actually runs.
    const grouped = useMemo(() => {
        const visible = shiftFilter
            ? rows.filter(r => String(r.shift_id) === String(shiftFilter))
            : rows;
        const buckets = new Map();
        visible.forEach(row => {
            const key = row.shift_name
                ? `${row.shift_name} · ${row.shift_timing} · ${row.grace_minutes ?? 0} min grace`
                : 'No shift assigned';
            if (!buckets.has(key)) buckets.set(key, []);
            buckets.get(key).push(row);
        });
        return Array.from(buckets.entries());
    }, [rows, shiftFilter]);

    const hubName = useMemo(
        () => inventory?.find(inv => inv.value === hubId)?.name || '',
        [inventory, hubId]
    );

    const readOnly = monthLocked;

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />

            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 2, flexWrap: 'wrap', mb: 3
            }}>
                <Typography variant="h5">Daily Attendance</Typography>
                <Box sx={{
                    display: 'flex', gap: { xs: '8px', md: '14px' }, alignItems: 'center',
                    flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', md: 'auto' }
                }}>
                    <Box sx={{ width: { xs: '100%', sm: '190px' } }}>
                        <Calendar
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.value)}
                            readOnlyInput
                            showIcon
                            dateFormat="dd-mm-yy"
                            maxDate={new Date()}
                            style={{ width: '100%' }}
                        />
                    </Box>
                    <FormControl size="small" sx={{ minWidth: '150px' }}>
                        <InputLabel>Shift</InputLabel>
                        <Select value={shiftFilter} label="Shift" onChange={(e) => setShiftFilter(e.target.value)}>
                            <MenuItem value="">All shifts</MenuItem>
                            {shifts.map(s => (
                                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={handleMarkAllPresent} disabled={readOnly || saving}>
                        Mark all present
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={readOnly || saving || !editCount}>
                        {editCount ? `Save ${editCount} change${editCount > 1 ? 's' : ''}` : 'Save'}
                    </Button>
                </Box>
            </Box>

            {monthLocked && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
                    padding: '10px 16px', backgroundColor: '#FFF7E6',
                    border: '1px solid #FFD591', borderRadius: '8px'
                }}>
                    <LockOutlinedIcon sx={{ fontSize: 18, color: '#A85700' }} />
                    <Typography sx={{ fontSize: '12.5px', color: '#A85700' }}>
                        Payroll for <b>{dayjs(selectedDate).format('MMMM YYYY')}</b> is locked. Attendance for this
                        month is read-only so already-disbursed salary can't change.
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', mb: 3 }}>
                <StatCard label="Present" value={summary?.present ?? '—'} hint={`of ${summary?.total ?? '—'} on roll`} accent="borders.green" />
                <StatCard label="Late" value={summary?.late ?? '—'} hint="beyond grace" accent="#FA8C16" />
                <StatCard label="Absent" value={summary?.absent ?? '—'} hint="no punch, no leave" accent="borders.red" />
                <StatCard label="On leave / OD" value={summary?.on_leave ?? '—'} hint="approved in advance" accent="button.blue" />
                <StatCard label="Missing punch" value={summary?.missing_punch ?? '—'} hint="needs admin fix" accent="secondary.border" />
                <StatCard label="Not marked" value={summary?.not_marked ?? '—'} hint="nothing entered yet" accent="secondary.border" />
            </Box>

            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                        {dayjs(selectedDate).format('dddd, DD MMMM YYYY')}{hubName ? ` · ${hubName}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        {editCount ? `${editCount} unsaved change${editCount > 1 ? 's' : ''}` : 'no unsaved changes'}
                    </Typography>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Staff', 'Shift', 'Arrival', 'Dispersal', 'Worked', 'Flag', 'Status', 'Source', 'Remarks'].map((h, i) => (
                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', whiteSpace: 'nowrap' }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={9} sx={{ backgroundColor: 'white', height: '200px' }}>
                                    {[0, 1, 2].map(i => (
                                        <Skeleton key={i} animation="wave" sx={{ height: '46px', background: 'rgb(0 0 0 / 6%)' }} />
                                    ))}
                                </TableCell>
                            </TableRow>
                        ) : grouped.length ? grouped.map(([groupLabel, groupRows]) => (
                            <React.Fragment key={groupLabel}>
                                <TableRow>
                                    <TableCell colSpan={9} sx={{
                                        backgroundColor: '#E6F4FF', color: '#0071BC',
                                        fontWeight: 600, fontSize: '11.5px',
                                        textTransform: 'uppercase', letterSpacing: '.04em'
                                    }}>
                                        {groupLabel}
                                    </TableCell>
                                </TableRow>
                                {groupRows.map(row => {
                                    const locked = row.is_locked || readOnly;
                                    const dirty = Boolean(edits[row.staff_id]);
                                    const lateBeyondGrace = row.late_by_minutes > (row.grace_minutes ?? 0);
                                    const earlyBeyondGrace = row.early_by_minutes > (row.grace_minutes ?? 0);
                                    return (
                                        <TableRow key={row.staff_id} sx={dirty ? { backgroundColor: '#FFF8FB' } : undefined}>
                                            <TableCell sx={{ backgroundColor: 'inherit' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <Avatar sx={{ width: 26, height: 26, fontSize: '10px', bgcolor: 'background.table', color: 'button.blue' }}>
                                                        {initialsOf(row.full_name)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{row.full_name}</Typography>
                                                        <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>
                                                            {row.employee_id}{row.designation ? ` · ${row.designation}` : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ backgroundColor: 'inherit', whiteSpace: 'nowrap', fontSize: '12px' }}>
                                                {row.shift_timing || '—'}
                                            </TableCell>
                                            <TableCell sx={{ backgroundColor: 'inherit' }}>
                                                <TextField
                                                    type="time" size="small" disabled={locked}
                                                    value={valueFor(row, 'arrival_time')}
                                                    onChange={(e) => handleEdit(row, 'arrival_time', e.target.value)}
                                                    sx={{ width: '110px', '& input': { fontSize: '12px', padding: '6px 8px' } }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ backgroundColor: 'inherit' }}>
                                                <TextField
                                                    type="time" size="small" disabled={locked}
                                                    value={valueFor(row, 'dispersal_time')}
                                                    onChange={(e) => handleEdit(row, 'dispersal_time', e.target.value)}
                                                    sx={{ width: '110px', '& input': { fontSize: '12px', padding: '6px 8px' } }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ backgroundColor: 'inherit', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                {formatWorked(row.worked_minutes)}
                                            </TableCell>
                                            <TableCell sx={{ backgroundColor: 'inherit' }}>
                                                {lateBeyondGrace ? (
                                                    <StatusChip status="late" />
                                                ) : earlyBeyondGrace ? (
                                                    <Tooltip title={`Left ${row.early_by_minutes} min before shift end`} arrow>
                                                        <Box><StatusChip status="half_day" /></Box>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ backgroundColor: 'inherit' }}>
                                                <StatusChip status={row.status} />
                                            </TableCell>
                                            <TableCell sx={{ backgroundColor: 'inherit' }}>
                                                <Typography sx={{ fontSize: '10px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                                                    {row.source || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ backgroundColor: 'inherit', minWidth: '180px' }}>
                                                {row.is_locked ? (
                                                    <Typography sx={{ fontSize: '11px', color: 'button.blue' }}>
                                                        {row.lock_reason}
                                                    </Typography>
                                                ) : (
                                                    <TextField
                                                        size="small" placeholder="—" disabled={locked}
                                                        value={valueFor(row, 'remarks')}
                                                        onChange={(e) => handleEdit(row, 'remarks', e.target.value)}
                                                        sx={{ width: '100%', '& input': { fontSize: '12px', padding: '6px 8px' } }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </React.Fragment>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={9} sx={{
                                    textAlign: 'center', backgroundColor: 'white',
                                    height: '200px', fontWeight: 700
                                }}>
                                    No active staff for this hub on this date
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
